import { AxiosResponse } from 'axios';
import { PayloadAction, AnyAction, createSlice } from '@reduxjs/toolkit';

import { API_POST_TIMEOUT } from 'constants/params';
import { setStoryConfig, StoryConfig } from 'services/story.service';
import { setStoryboard, StoryBoard } from 'services/storyboard.service';
import { FileStatus, SetFileConfig, setFileConfig } from 'services/file.service';
import { passAudioMarkers, setAudioMarkerPreset } from 'services/audio.service';
import { SetClipsReq, setClips } from 'services/clip.service';
import { LogLevel } from 'services/logger.service';
import { AppThunk } from 'store/store';
import { createSafeAsyncThunk } from 'store/store.helper';
import { FileConfigsMap, updateFileStatus } from 'components/app/_mediator/app-files.slice';
import {
  ThumbnailStatus,
  Thumbnails,
  handleClipThumbnails,
  updateClipThumbnails,
} from 'components/app/_mediator/clip-thumb/clip-thumb.slice';
import { getSbTracks, isNull } from 'components/app/_helpers/_app.helper';
import { ClipsMap } from 'components/app/editor/_mediator/editor.slice';
import { sliceSbTrack } from 'components/app/editor/_mediator/editor-clips/editor-clips-sb.helper';
import { markersConversion } from 'components/app/editor/_mediator/editor-markers/editor-markers.helper';
import { appLogger, cleanRecentActions, UserActionData } from 'components/app/_mediator/app-logger.slice';
import { Monitor, resetMonitor, setTitleCreditsData, TitleCreditsData } from './monitor.slice';
import { BackupTitle } from '../backup/backup.slice';

const SRC = 'monitor-req.slice.ts';

let resolver: ((value: unknown) => void) | null = null;

export interface Payload {
  storyId: StoryId;
  projectId?: number;
  markers?: MarkerId[];
  storyBoard?: Partial<StoryBoard>;
  files?: SetFileConfig[];
  clips?: SetClipsReq['data'];
  storyConfig?: StoryConfig;
  markerPreset?: MarkerPresetId;
  credits?: TitleCreditsData;
  title?: TitleCreditsData;
  log: UserActionData[];
  actions: BackupTitle[];
}

interface State {
  rejectData: string | null;
  pending: Payload[] | null;
  buffer: Payload[] | null;
}

const initialState: State = {
  rejectData: null,
  pending: null,
  buffer: null,
};

// H E L P E R S

const handlePayloads = (payloads: Payload[]) => {
  const mergePayloads = (curr: Payload, newPayload: Payload) => {
    const { markers, storyBoard, clips, storyConfig, actions } = newPayload;
    const res = { ...curr };
    if (markers) res.markers = markers;
    if (storyBoard) res.storyBoard = { ...curr.storyBoard, ...storyBoard };
    if (clips) {
      if (!res.clips) res.clips = clips;
      else
        Object.keys(clips).forEach((clipId) => {
          if (res.clips![clipId]) {
            res.clips![clipId] = { ...curr.clips![clipId], ...clips[clipId] };
          } else res.clips = { ...curr.clips, [clipId]: clips[clipId] };
        });
    }
    if (storyConfig) res.storyConfig = storyConfig;
    res.actions = [...curr.actions, ...actions];
    return res;
  };
  const mergeFiles = (payloads: Payload[]) => {
    return payloads.reduce((prev, curr) => {
      if (!curr.files) return prev;
      if (prev.length === 0) return curr.files;
      const result = [...prev];
      curr.files.forEach((file) => {
        const index = prev.findIndex((el) => el.fileId === file.fileId);
        if (index !== -1) result[index] = file;
        else result.push(file);
      });
      return result;
    }, [] as SetFileConfig[]);
  };

  const resultPayloads: Payload[] = [];

  payloads.forEach((payload) => {
    const index = resultPayloads.findIndex((el) => el.storyId === payload.storyId);
    if (index !== -1) resultPayloads[index] = mergePayloads(resultPayloads[index], payload);
    else resultPayloads.push(payload);
  });

  return {
    payloads: resultPayloads,
    files: mergeFiles(payloads),
  };
};

export const aggregateData = (data: {
  monitor: Monitor;
  storyId: StoryId;
  storyBoard: StoryBoard;
  clipsMap: ClipsMap | null;
  clipThumbnails: Thumbnails;
  markers: MarkerId[] | null;
  fileConfigs: FileConfigsMap | null;
  storyConfig: StoryConfig | null;
  selectedMarkerPreset: MarkerPresetId | null;
  log: UserActionData[];
}) => {
  const {
    monitor,
    storyId,
    storyBoard,
    clipsMap,
    clipThumbnails,
    markers,
    fileConfigs,
    storyConfig,
    selectedMarkerPreset,
    log,
  } = data;

  const {
    actions,
    isMarkersUpdated,
    isRangeMarkersUpdated,
    isMarkerPresetUpdated,
    isStoryConfigUpdated,
    isCreditsUpdated,
    isTitleUpdated,
    updatedClips,
    updatedFiles,
    updatedTracks,
  } = monitor;

  const payload: Payload = { storyId, log, actions };

  if (!clipsMap || !markers || !fileConfigs || !storyConfig) {
    console.error(`[${SRC}] aggregateAppState`);
    return payload;
  }
  if (isMarkersUpdated)
    payload.markers = markersConversion(
      'toBE',
      markers,
      storyConfig.transitionTime,
      storyConfig.clipTransitionAlignment
    );

  if (isMarkerPresetUpdated) payload.markerPreset = selectedMarkerPreset!;

  if (updatedFiles.length) {
    payload.files = [];
    updatedFiles.forEach((fileId) => {
      const file = fileConfigs[fileId];
      if (file) payload.files!.push(file);
      else console.error(`[${SRC}] aggregateAppState: file not found`);
    });
    payload.projectId = payload.files[0].projectId;
  }
  if (updatedTracks) {
    payload.storyBoard = {};
    getSbTracks().forEach((track) => {
      if (updatedTracks[track]) payload.storyBoard![track] = sliceSbTrack(storyBoard[track]);
    });
  }
  if (updatedClips.length) {
    payload.clips = {};
    updatedClips.forEach((clipId) => {
      const thumbnail = clipThumbnails[clipId];
      const clip = clipsMap[clipId];
      if (clip) {
        payload.clips![clipId] = {
          clipGroupId: clip.clipGroupId,
          config: clip.config,
        };
        if (thumbnail.status === ThumbnailStatus.uploaded) {
          payload.clips![clipId].thumbnailUrl = thumbnail.url;
        }
      }
    });
  }
  if (isStoryConfigUpdated || isRangeMarkersUpdated || isCreditsUpdated || isTitleUpdated) {
    payload.storyConfig = storyConfig;
    if (isCreditsUpdated)
      payload.credits = {
        storyId,
        text: storyConfig.creditsText,
        preset: storyConfig.creditsTextPreset,
        aspectRatio: storyConfig.aspectRatio,
        key: 'credits',
      };
    if (isTitleUpdated)
      payload.title = {
        storyId,
        text: storyConfig.titleText,
        preset: storyConfig.titleTextPreset,
        aspectRatio: storyConfig.aspectRatio,
        key: 'title',
      };
  }

  return payload;
};

// T H U N K S

export const aggregator =
  (selectedStory: StoryId | null): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const monitor = state.monitor;

    if (!monitor.isDirty) return;

    const { storyBoard, clipsMap, markers, selectedMarkerPreset, storyConfig } = state.editor;
    const { clipThumbnails } = state.clipThumb;
    const log = state.app__logger.recentActions;
    const fileConfigs = state.app__files.configs;
    const storyId = selectedStory ?? state.projects.selectedStory;

    if (isNull(storyId)) return;

    const payload = aggregateData({
      storyId,
      storyBoard,
      clipsMap,
      monitor,
      markers,
      fileConfigs,
      clipThumbnails,
      storyConfig,
      selectedMarkerPreset,
      log,
    });

    dispatch(cleanRecentActions());
    dispatch(monitorReq.actions.pushBuffer(payload));
    dispatch(resetMonitor());
  };
//

export const passMonitor = createSafeAsyncThunk('monitorReq/passMonitor', async (payload: Payload[], { dispatch }) => {
  const { payloads, files } = handlePayloads(payload);
  const proms: Promise<AxiosResponse | AnyAction | void>[] = [];
  payloads.forEach((payload) => {
    const storyId = payload.storyId;
    if (payload.markers) proms.push(passAudioMarkers({ storyId, ids: payload.markers }));
    if (payload.storyBoard) proms.push(setStoryboard({ storyId, ...payload.storyBoard }));
    if (payload.storyConfig) proms.push(setStoryConfig({ ...payload.storyConfig, storyId: payload.storyId }));
    if (payload.markerPreset) proms.push(setAudioMarkerPreset(storyId, payload.markerPreset));
    if (payload.clips)
      proms.push(
        setClips({ storyId, data: payload.clips }).then((res) => {
          dispatch(updateClipThumbnails(handleClipThumbnails(res)));
        })
      );
    proms.push(
      dispatch(
        appLogger({
          level: LogLevel.Info,
          error: { name: 'appLogger', message: JSON.stringify(payload) },
        })
      )
    );
  });
  files.forEach((file) => {
    proms.push(
      setFileConfig(file).then((res) => {
        if (res.data.isProcessing)
          dispatch(updateFileStatus({ fileId: file.fileId, status: FileStatus.PendingProcessingEffects }));
      })
    );
  });
  proms.push(
    (async () => {
      for (const payload of payloads) {
        if (payload.title) await dispatch(waitTitleCreditsGenerator(payload.title));
        if (payload.credits) await dispatch(waitTitleCreditsGenerator(payload.credits));
      }
    })()
  );
  await Promise.all(proms);
  await new Promise((resolve) => setTimeout(resolve, API_POST_TIMEOUT));
  dispatch(cleanPending());
});

const waitTitleCreditsGenerator = createSafeAsyncThunk(
  'monitorReq/runTitleCreditsGenerator',
  async (payload: TitleCreditsData, { dispatch }) => {
    dispatch(setTitleCreditsData(payload));
    return new Promise<unknown>((resolve) => {
      resolver = resolve;
    });
  }
);

export const resolveTitleCreditsGenerator = createSafeAsyncThunk(
  'monitorReq/resolveTitleCreditsGenerator',
  async (_, { dispatch }) => {
    if (resolver) resolver('');
    dispatch(setTitleCreditsData(null));
  }
);

// S L I C E

export const monitorReq = createSlice({
  name: 'monitorReq',
  initialState,
  reducers: {
    setBuffer: (state, action: PayloadAction<Payload[]>) => {
      state.buffer = action.payload;
    },
    pushBuffer: (state, action: PayloadAction<Payload>) => {
      if (isNull(state.buffer)) state.buffer = [];
      state.buffer.push(action.payload);
    },
    addPending: (state) => {
      state.pending = state.buffer;
      state.buffer = null;
    },
    cleanPending: (state) => {
      state.pending = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(passMonitor.rejected, (state, action) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      state.rejectData = (action.payload as any)?.message ?? 'Unknown error';
    });
  },
});

export const { setBuffer, cleanPending, addPending } = monitorReq.actions;

export default monitorReq.reducer;
